/**
 * One row of the Notification Center (#1093): unread dot, icon, title, text,
 * meta line; read/more actions on hover or keyboard focus (the more menu marks
 * read rows unread again, #1135, and deletes); an inline button
 * for rows that wait on the user. A bundle (several rows with one
 * `group_key`) renders as one row that expands to its items.
 */
import React, { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { AlertCircle, Bug, Check, CheckCircle, ChevronDown, Info, Mail, MoreHorizontal, Trash2, XCircle } from 'lucide-react'
import type { Notification } from '@/services/notification-service'
import {
  notificationActor,
  notificationCategory,
  notificationOutcome,
  resolveNotificationTarget,
} from '@/lib/utils/notificationPayload'
import { initials, type NotificationEntry } from '@/lib/utils/notificationGrouping'
import { formatExact, formatWhen, notificationMeta, notificationPreview, primaryActionLabel } from './notificationDisplay'
import { ACCENT, ACCENT_FILL, FOCUS_RING, OPAQUE_SURFACE, STATUS, tint } from './notificationStyles'

/** Outcome colours the icon; a person behind the event shows as initials. */
export function NotificationIcon({ notification, size = 'md' }: { notification: Notification; size?: 'sm' | 'md' }) {
  const outcome = notificationOutcome(notification)
  const actorName = notificationActor(notification)?.name
  const box = size === 'sm' ? 'h-7 w-7' : 'h-[34px] w-[34px]'
  const glyph = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'
  const base = `${box} flex-shrink-0 flex items-center justify-center text-xs font-semibold`

  if (outcome === 'success') {
    return <span aria-hidden className={`${base} rounded-md`} style={tint(STATUS.success)}><CheckCircle className={glyph} /></span>
  }
  if (outcome === 'failure') {
    return <span aria-hidden className={`${base} rounded-md`} style={tint(STATUS.danger)}><XCircle className={glyph} /></span>
  }
  if (actorName) {
    return <span aria-hidden className={`${base} rounded-full`} style={tint(ACCENT)}>{initials(actorName)}</span>
  }
  if (outcome === 'action_required') {
    return <span aria-hidden className={`${base} rounded-md`} style={tint(STATUS.warning)}><AlertCircle className={glyph} /></span>
  }
  if (notificationCategory(notification) === 'bug_reports') {
    return <span aria-hidden className={`${base} rounded-md`} style={tint(STATUS.warning)}><Bug className={glyph} /></span>
  }
  return (
    <span aria-hidden className={`${base} rounded-md bg-[var(--menu-item-hover)] text-[var(--text-secondary)]`}>
      <Info className={glyph} />
    </span>
  )
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/** "3 neue Antworten zu „PDF-Vorschau leer“" — what a bundle is about, counted. */
export function bundleTitle(entry: NotificationEntry, t: TFunction, language: string): string {
  const { latest, items, unreadCount } = entry
  const reportTitle = str(latest.data?.report_title)
  const subject =
    str(latest.data?.report_number) ??
    (reportTitle ? t('notificationCenter.bundle.quoted', { text: reportTitle }) : notificationPreview(t, latest, language).title)
  const replies = items.every((n) => n.type === 'bug_report_reply')
  const allNew = unreadCount === items.length
  const key = replies ? (allNew ? 'newReplies' : 'replies') : allNew ? 'newUpdates' : 'updates'
  return t(`notificationCenter.bundle.${key}`, { count: items.length, subject })
}

interface NotificationRowProps {
  entry: NotificationEntry
  onOpen: (notification: Notification) => void
  onMarkRead: (items: Notification[]) => void
  onMarkUnread: (items: Notification[]) => void
  onDelete: (items: Notification[]) => void
  now?: Date
  nested?: boolean
}

export function NotificationRow({ entry, onOpen, onMarkRead, onMarkUnread, onDelete, now, nested = false }: NotificationRowProps) {
  const { t, i18n } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const actionsRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  const { latest, items, unreadCount } = entry
  const isBundle = items.length > 1
  const unread = unreadCount > 0
  const readItems = items.filter((n) => n.is_read)
  const language = i18n.language
  const text = notificationPreview(t, latest, language)
  const title = isBundle ? bundleTitle(entry, t, language) : text.title
  const meta = notificationMeta(latest, t)
  const clickable = resolveNotificationTarget(latest) !== null
  const actionRequired = unread && clickable && notificationOutcome(latest) === 'action_required'

  const content = (
    <span className="flex flex-col gap-[3px] min-w-0">
      <span className="flex items-baseline justify-between gap-2">
        <span
          className={`text-[13px] leading-snug text-[var(--text-color)] ${unread ? 'font-semibold' : 'font-medium'}`}
        >
          {title}
        </span>
        <time
          dateTime={latest.created_at}
          title={formatExact(latest.created_at, language)}
          className="text-xs text-[var(--text-secondary)] whitespace-nowrap"
        >
          {formatWhen(latest.created_at, t, language, now)}
        </time>
      </span>
      {text.message && (
        <span className="text-[13px] leading-[1.45] text-[var(--text-secondary)] line-clamp-2">{text.message}</span>
      )}
      {meta && <span className="text-xs text-[var(--text-secondary)] opacity-80">{meta}</span>}
    </span>
  )

  const closeMenuOnBlur = (event: React.FocusEvent) => {
    if (!actionsRef.current?.contains(event.relatedTarget as Node | null)) setMenuOpen(false)
  }

  return (
    <li
      data-notification-row={nested ? undefined : ''}
      className={`${nested ? 'group/sub' : 'group/row'} relative flex items-start gap-2.5 ${nested ? 'py-2 pl-2 pr-3' : 'py-2.5 pl-2.5 pr-4'} transition-colors ${unread && !nested ? 'bg-[color-mix(in_srgb,var(--theme-interactive-color)_6%,transparent)]' : ''} hover:bg-[var(--menu-item-hover)] focus-within:bg-[var(--menu-item-hover)]`}
    >
      <span
        aria-hidden
        data-testid={unread ? 'unread-dot' : undefined}
        className="mt-[15px] h-1.5 w-1.5 flex-shrink-0 rounded-full"
        style={{ background: unread ? ACCENT : 'transparent' }}
      />
      <NotificationIcon notification={latest} size={nested ? 'sm' : 'md'} />

      <div className="flex-1 min-w-0 flex flex-col">
        {clickable ? (
          <button
            type="button"
            onClick={() => onOpen(latest)}
            className={`w-full text-left rounded-md ${FOCUS_RING}`}
          >
            {unread && <span className="sr-only">{t('notificationCenter.unreadPrefix')} </span>}
            {content}
          </button>
        ) : (
          <div>
            {unread && <span className="sr-only">{t('notificationCenter.unreadPrefix')} </span>}
            {content}
          </div>
        )}

        {(actionRequired || isBundle) && (
          <div className="flex flex-wrap items-center gap-2 pt-1.5">
            {actionRequired && (
              <button
                type="button"
                onClick={() => onOpen(latest)}
                className={`h-[30px] px-3 rounded-md text-xs font-semibold transition-[filter,transform] duration-150 ease-[cubic-bezier(.2,0,.2,1)] hover:brightness-110 active:translate-y-px motion-reduce:transition-none ${FOCUS_RING} focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--window-background)]`}
                style={ACCENT_FILL}
              >
                {primaryActionLabel(latest, t)}
              </button>
            )}
            {isBundle && (
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
                aria-controls={listId}
                className={`h-[26px] px-2 rounded-md flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-color)] ${FOCUS_RING}`}
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
                {expanded
                  ? t('notificationCenter.bundle.collapse')
                  : t('notificationCenter.bundle.expand', { count: items.length })}
              </button>
            )}
          </div>
        )}

        {isBundle && expanded && (
          <ul id={listId} className="mt-1 -ml-2 border-l border-[var(--window-border-color)]">
            {items.map((item) => (
              <NotificationRow
                key={item.id}
                entry={{ key: item.id, items: [item], latest: item, unreadCount: item.is_read ? 0 : 1 }}
                onOpen={onOpen}
                onMarkRead={onMarkRead}
                onMarkUnread={onMarkUnread}
                onDelete={onDelete}
                now={now}
                nested
              />
            ))}
          </ul>
        )}
      </div>

      <div
        ref={actionsRef}
        onBlur={closeMenuOnBlur}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && menuOpen) {
            // Handled: the panel's Escape listener leaves it alone.
            event.preventDefault()
            event.stopPropagation()
            setMenuOpen(false)
          }
        }}
        style={OPAQUE_SURFACE}
        className={`absolute right-3 top-2 flex gap-0.5 p-0.5 rounded-lg border border-[var(--window-border-color)] shadow-sm transition-opacity ${
          menuOpen
            ? 'opacity-100'
            : nested
              ? 'opacity-0 group-hover/sub:opacity-100 group-focus-within/sub:opacity-100'
              : 'opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100'
        }`}
      >
        {unread && (
          <button
            type="button"
            onClick={() => onMarkRead(items.filter((n) => !n.is_read))}
            aria-label={isBundle ? t('notificationCenter.markBundleRead') : t('notificationCenter.markRead')}
            title={isBundle ? t('notificationCenter.markBundleRead') : t('notificationCenter.markRead')}
            className={`h-7 w-7 flex items-center justify-center rounded-md text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] ${FOCUS_RING}`}
          >
            <Check className="h-[15px] w-[15px]" aria-hidden />
          </button>
        )}
        <button
          type="button"
          onClick={() => setMenuOpen((value) => !value)}
          aria-label={t('notificationCenter.moreActions')}
          title={t('notificationCenter.moreActions')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className={`h-7 w-7 flex items-center justify-center rounded-md text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] ${FOCUS_RING}`}
        >
          <MoreHorizontal className="h-[15px] w-[15px]" aria-hidden />
        </button>
        {menuOpen && (
          <div
            role="menu"
            style={OPAQUE_SURFACE}
            className="absolute right-0 top-full mt-1 z-10 min-w-[10rem] w-max p-1 rounded-lg border border-[var(--window-border-color)] shadow-lg"
          >
            {readItems.length > 0 && (
              <button
                type="button"
                role="menuitem"
                autoFocus
                data-testid="notification-mark-unread"
                onClick={() => {
                  setMenuOpen(false)
                  onMarkUnread(readItems)
                }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left whitespace-nowrap text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] ${FOCUS_RING}`}
              >
                <Mail className="h-4 w-4 text-[var(--text-secondary)]" aria-hidden />
                {isBundle ? t('notificationCenter.markBundleUnread') : t('notificationCenter.markUnread')}
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              autoFocus={readItems.length === 0}
              onClick={() => {
                setMenuOpen(false)
                onDelete(items)
              }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left whitespace-nowrap text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] ${FOCUS_RING}`}
            >
              <Trash2 className="h-4 w-4 text-[var(--text-secondary)]" aria-hidden />
              {isBundle ? t('notificationCenter.deleteBundle') : t('notificationCenter.delete')}
            </button>
          </div>
        )}
      </div>
    </li>
  )
}
